/**
 * Form controls. A field has a `name`; what the person enters is that name's value in the app's `values`, which a button's
 * declared action carries (`{{values.name}}`). A field may also carry `onChange`, a declared action that runs as soon as the value
 * changes (a filter, a search). A field never reaches the network: a drop zone's files go to the declared action as attachments,
 * size-capped, and nowhere else.
 * OURS: the workbench defines this file; AIOE mirrors it.
 */
import { UI_LIMITS } from "../uiBase.js";
import { control } from "./kit.js";
/** The properties every field shares. */
const base = (k) => ({ name: k.name, label: k.label.min(1), description: k.hint.optional(), required: k.z.boolean().optional(), disabled: k.z.boolean().optional(), onChange: k.action.optional() });
/** A field that chooses among options: literal or a binding's list (`{ value, label }`, remapped by `keys`). */
const choice = (k) => ({ ...base(k), options: k.options().optional(), optionsFrom: k.path.optional(), keys: k.keys, placeholder: k.label.optional(), default: k.z.string().max(60).optional() });
const OPTIONS = [
    { value: "a", label: "Alpha", description: "The first" },
    { value: "b", label: "Beta" },
    { value: "c", label: "Gamma" },
];
const range = (k) => ({ min: k.num.optional(), max: k.num.optional(), step: k.z.number().positive().optional() });
export const FORM_CONTROLS = [
    control({
        type: "text-area",
        group: "forms",
        heroui: "@heroui/react TextArea (in TextField)",
        summary: "text-area { name, label, description?, placeholder?, default?, rows?: 2-12, maxLength?, required?, onChange? }",
        example: { type: "text-area", name: "notes", label: "Notes", placeholder: "What should we know?", rows: 4 },
        field: "text",
        shape: (k) => ({ ...base(k), placeholder: k.label.optional(), default: k.text.optional(), rows: k.z.number().int().min(2).max(12).optional(), maxLength: k.z.number().int().min(1).max(UI_LIMITS.text).optional() }),
    }),
    control({
        type: "search-field",
        group: "forms",
        heroui: "@heroui/react SearchField",
        summary: "search-field { name, label, placeholder?, default?, onChange? } — a search box with a clear button; onChange runs as the person types (a filter)",
        example: { type: "search-field", name: "q", label: "Search tasks", placeholder: "Search…" },
        field: "text",
        shape: (k) => ({ ...base(k), placeholder: k.label.optional(), default: k.z.string().max(UI_LIMITS.label).optional() }),
    }),
    control({
        type: "input-group",
        group: "forms",
        heroui: "@heroui/react InputGroup",
        summary: "input-group { name, label, prefix?, suffix?, placeholder?, default?, inputType?: text|email|tel|search, required?, onChange? } — an input with text before or after it ($, kg, @)",
        example: { type: "input-group", name: "budget", label: "Budget", prefix: "$", suffix: "AUD", placeholder: "0.00" },
        field: "text",
        shape: (k) => ({ ...base(k), prefix: k.z.string().max(12).optional(), suffix: k.z.string().max(12).optional(), placeholder: k.label.optional(), default: k.z.string().max(UI_LIMITS.label).optional(), inputType: k.z.enum(["text", "email", "tel", "search"]).optional() }),
    }),
    control({
        type: "number-field",
        group: "forms",
        heroui: "@heroui/react NumberField",
        summary: "number-field { name, label, min?, max?, step?, default?, format?: decimal|currency|percent, currency?, required?, onChange? }",
        example: { type: "number-field", name: "seats", label: "Seats", min: 1, max: 50, step: 1, default: 5 },
        field: "number",
        shape: (k) => ({ ...base(k), ...range(k), default: k.num.optional(), format: k.z.enum(["decimal", "currency", "percent"]).optional(), currency: k.z.string().regex(/^[A-Z]{3}$/).optional() }),
    }),
    control({
        type: "number-stepper",
        group: "forms",
        heroui: "@heroui-pro/react/number-stepper NumberStepper",
        summary: "number-stepper { name, label, min?, max?, step?, default?, onChange? } — a number with minus and plus buttons",
        example: { type: "number-stepper", name: "copies", label: "Copies", min: 1, max: 10, default: 1 },
        field: "number",
        shape: (k) => ({ ...base(k), ...range(k), default: k.num.optional() }),
    }),
    control({
        type: "select",
        group: "forms",
        heroui: "@heroui/react Select",
        summary: "select { name, label, options[{ value, label, description? }] | optionsFrom, keys?, default?, placeholder?, required?, onChange? } — a dropdown",
        example: { type: "select", name: "terms", label: "Terms", options: OPTIONS, default: "a" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "autocomplete",
        group: "forms",
        heroui: "@heroui/react Autocomplete",
        summary: "autocomplete { name, label, options | optionsFrom, keys?, default?, placeholder?, onChange? } — a dropdown with a search box",
        example: { type: "autocomplete", name: "owner", label: "Owner", options: OPTIONS, placeholder: "Search people" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "combo-box",
        group: "forms",
        heroui: "@heroui/react ComboBox",
        summary: "combo-box { name, label, options | optionsFrom, keys?, default?, placeholder?, onChange? } — type to filter, then pick",
        example: { type: "combo-box", name: "repo", label: "Repository", options: OPTIONS },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "inline-select",
        group: "forms",
        heroui: "@heroui-pro/react InlineSelect",
        summary: "inline-select { name, label, options | optionsFrom, keys?, default?, onChange? } — a select that sits in a sentence or a toolbar",
        example: { type: "inline-select", name: "range", label: "Show", options: OPTIONS, default: "a" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "native-select",
        group: "forms",
        heroui: "@heroui-pro/react NativeSelect",
        summary: "native-select { name, label, options | optionsFrom, keys?, default?, placeholder?, onChange? } — the system's own dropdown (best for long lists and phones)",
        example: { type: "native-select", name: "country", label: "Country", options: OPTIONS },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "cell-select",
        group: "forms",
        heroui: "@heroui-pro/react CellSelect",
        summary: "cell-select { name, label, options | optionsFrom, keys?, default?, description?, onChange? } — a settings row with a select at its end",
        example: { type: "cell-select", name: "theme", label: "Theme", description: "How the app looks", options: OPTIONS, default: "a" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "checkbox",
        group: "forms",
        heroui: "@heroui/react Checkbox",
        summary: "checkbox { name, label, description?, default?, onChange? }",
        example: { type: "checkbox", name: "agree", label: "Tell the team", default: false },
        field: "boolean",
        shape: (k) => ({ ...base(k), default: k.z.boolean().optional() }),
    }),
    control({
        type: "checkbox-group",
        group: "forms",
        heroui: "@heroui/react CheckboxGroup",
        summary: "checkbox-group { name, label, options | optionsFrom, keys?, default?: string[], onChange? } — several checkboxes; the value is the list of ticked values",
        example: { type: "checkbox-group", name: "areas", label: "Areas", options: OPTIONS, default: ["a"] },
        field: "options",
        sources: [["options", "optionsFrom"]],
        shape: (k) => ({ ...choice(k), default: k.z.array(k.z.string().max(60)).max(UI_LIMITS.controlOptions).optional() }),
    }),
    control({
        type: "checkbox-button-group",
        group: "forms",
        heroui: "@heroui-pro/react CheckboxButtonGroup",
        summary: "checkbox-button-group { name, label, options | optionsFrom, keys?, default?: string[], onChange? } — checkboxes drawn as selectable buttons",
        example: { type: "checkbox-button-group", name: "days", label: "Days", options: OPTIONS, default: ["a", "b"] },
        field: "options",
        sources: [["options", "optionsFrom"]],
        shape: (k) => ({ ...choice(k), default: k.z.array(k.z.string().max(60)).max(UI_LIMITS.controlOptions).optional() }),
    }),
    control({
        type: "radio-group",
        group: "forms",
        heroui: "@heroui/react RadioGroup",
        summary: "radio-group { name, label, options | optionsFrom, keys?, default?, orientation?: vertical|horizontal, onChange? }",
        example: { type: "radio-group", name: "plan", label: "Plan", options: OPTIONS, default: "a" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: (k) => ({ ...choice(k), orientation: k.z.enum(["vertical", "horizontal"]).optional() }),
    }),
    control({
        type: "radio-button-group",
        group: "forms",
        heroui: "@heroui-pro/react RadioButtonGroup",
        summary: "radio-button-group { name, label, options | optionsFrom, keys?, default?, onChange? } — radios drawn as selectable cards or buttons",
        example: { type: "radio-button-group", name: "size", label: "Size", options: OPTIONS, default: "b" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: choice,
    }),
    control({
        type: "switch",
        group: "forms",
        heroui: "@heroui/react Switch",
        summary: "switch { name, label, description?, default?, onChange? } — an on-off switch",
        example: { type: "switch", name: "notify", label: "Notify me", default: true },
        field: "boolean",
        shape: (k) => ({ ...base(k), default: k.z.boolean().optional() }),
    }),
    control({
        type: "cell-switch",
        group: "forms",
        heroui: "@heroui-pro/react CellSwitch",
        summary: "cell-switch { name, label, description?, default?, onChange? } — a settings row with a switch at its end",
        example: { type: "cell-switch", name: "sync", label: "Keep in sync", description: "Every five minutes", default: true },
        field: "boolean",
        shape: (k) => ({ ...base(k), default: k.z.boolean().optional() }),
    }),
    control({
        type: "slider",
        group: "forms",
        heroui: "@heroui/react Slider",
        summary: "slider { name, label, min?, max?, step?, default?, onChange? } — drag to a number",
        example: { type: "slider", name: "volume", label: "Volume", min: 0, max: 100, step: 5, default: 40 },
        field: "number",
        shape: (k) => ({ ...base(k), ...range(k), default: k.num.optional() }),
    }),
    control({
        type: "cell-slider",
        group: "forms",
        heroui: "@heroui-pro/react CellSlider",
        summary: "cell-slider { name, label, min?, max?, step?, default?, description?, onChange? } — a settings row with a slider",
        example: { type: "cell-slider", name: "limit", label: "Daily limit", min: 0, max: 20, step: 1, default: 5 },
        field: "number",
        shape: (k) => ({ ...base(k), ...range(k), default: k.num.optional() }),
    }),
    control({
        type: "segment",
        group: "forms",
        heroui: "@heroui-pro/react Segment",
        summary: "segment { name, options[{ value, label }] | optionsFrom, keys?, default?, size?: sm|md|lg, onChange? } — a segmented control (the value is the chosen option)",
        example: { type: "segment", name: "view", label: "View", options: OPTIONS, default: "a" },
        field: "option",
        sources: [["options", "optionsFrom"]],
        shape: (k) => ({ ...choice(k), size: k.z.enum(["sm", "md", "lg"]).optional() }),
    }),
    control({
        type: "date-field",
        group: "forms",
        heroui: "@heroui/react DateField",
        summary: "date-field { name, label, default?: 2026-10-25, min?, max?, required?, onChange? } — type a date; the value is an ISO date",
        example: { type: "date-field", name: "due", label: "Due", default: "2026-11-01" },
        field: "date",
        shape: (k) => ({ ...base(k), default: k.date.optional(), min: k.date.optional(), max: k.date.optional() }),
    }),
    control({
        type: "date-picker",
        group: "forms",
        heroui: "@heroui/react DatePicker",
        summary: "date-picker { name, label, default?, min?, max?, required?, onChange? } — a date field with a calendar popover",
        example: { type: "date-picker", name: "start", label: "Start", default: "2026-10-26" },
        field: "date",
        shape: (k) => ({ ...base(k), default: k.date.optional(), min: k.date.optional(), max: k.date.optional() }),
    }),
    control({
        type: "date-range-picker",
        group: "forms",
        heroui: "@heroui/react DateRangePicker",
        summary: "date-range-picker { name, label, default?: \"2026-10-26/2026-10-30\", min?, max?, onChange? } — two dates; the value is start/end",
        example: { type: "date-range-picker", name: "window", label: "Window", default: "2026-10-26/2026-10-30" },
        field: "date-range",
        shape: (k) => ({ ...base(k), default: k.z.string().regex(/^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/).optional(), min: k.date.optional(), max: k.date.optional() }),
    }),
    control({
        type: "time-field",
        group: "forms",
        heroui: "@heroui/react TimeField",
        summary: "time-field { name, label, default?: 14:30, hour24?, onChange? }",
        example: { type: "time-field", name: "at", label: "At", default: "09:30", hour24: true },
        field: "time",
        shape: (k) => ({ ...base(k), default: k.time.optional(), hour24: k.z.boolean().optional() }),
    }),
    control({
        type: "calendar",
        group: "forms",
        heroui: "@heroui/react Calendar",
        summary: "calendar { name, label, default?, min?, max?, onChange? } — a month to pick one day from",
        example: { type: "calendar", name: "day", label: "Pick a day", default: "2026-10-27" },
        field: "date",
        shape: (k) => ({ ...base(k), default: k.date.optional(), min: k.date.optional(), max: k.date.optional() }),
    }),
    control({
        type: "range-calendar",
        group: "forms",
        heroui: "@heroui/react RangeCalendar",
        summary: "range-calendar { name, label, default?: \"start/end\", min?, max?, onChange? } — a month to pick a span of days from",
        example: { type: "range-calendar", name: "span", label: "Pick the days", default: "2026-10-27/2026-10-29" },
        field: "date-range",
        shape: (k) => ({ ...base(k), default: k.z.string().regex(/^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/).optional(), min: k.date.optional(), max: k.date.optional() }),
    }),
    control({
        type: "tag-group",
        group: "forms",
        heroui: "@heroui/react TagGroup",
        summary: "tag-group { name, label, options | optionsFrom, keys?, default?: string[], onChange? } — tags the person selects (the value is the selected values)",
        example: { type: "tag-group", name: "labels", label: "Labels", options: OPTIONS, default: ["b"] },
        field: "options",
        sources: [["options", "optionsFrom"]],
        shape: (k) => ({ ...choice(k), default: k.z.array(k.z.string().max(60)).max(UI_LIMITS.controlOptions).optional() }),
    }),
    control({
        type: "color-field",
        group: "forms",
        heroui: "@heroui/react ColorField",
        summary: "color-field { name, label, default?: #rrggbb, onChange? } — type a hex colour",
        example: { type: "color-field", name: "brand", label: "Brand colour", default: "#2f9e8f" },
        field: "color",
        shape: (k) => ({ ...base(k), default: k.color.optional() }),
    }),
    control({
        type: "color-picker",
        group: "forms",
        heroui: "@heroui/react ColorPicker",
        summary: "color-picker { name, label, default?: #rrggbb, onChange? } — a swatch that opens a colour area and sliders",
        example: { type: "color-picker", name: "accent", label: "Accent", default: "#7c5cff" },
        field: "color",
        shape: (k) => ({ ...base(k), default: k.color.optional() }),
    }),
    control({
        type: "color-swatch-picker",
        group: "forms",
        heroui: "@heroui/react ColorSwatchPicker",
        summary: "color-swatch-picker { name, label, colors[] (#rrggbb, at most 24), default?, onChange? } — pick one of a few colours",
        example: { type: "color-swatch-picker", name: "tag", label: "Tag colour", colors: ["#2f9e8f", "#7c5cff", "#f5a623", "#e5484d"], default: "#2f9e8f" },
        field: "color",
        shape: (k) => ({ ...base(k), colors: k.z.array(k.color).min(1).max(24), default: k.color.optional() }),
    }),
    control({
        type: "cell-color-picker",
        group: "forms",
        heroui: "@heroui-pro/react CellColorPicker",
        summary: "cell-color-picker { name, label, default?, description?, onChange? } — a settings row with a colour at its end",
        example: { type: "cell-color-picker", name: "bg", label: "Background", default: "#101820" },
        field: "color",
        shape: (k) => ({ ...base(k), default: k.color.optional() }),
    }),
    control({
        type: "rich-text-editor",
        group: "forms",
        heroui: "@heroui-pro/react/rich-text-editor RichTextEditor",
        summary: "rich-text-editor { name, label, default?: Markdown, placeholder?, onChange? } — formatted writing (bold, lists, links); the value is Markdown text",
        example: { type: "rich-text-editor", name: "body", label: "Update", default: "Shipped **today**.", placeholder: "Write the update" },
        field: "text",
        shape: (k) => ({ ...base(k), default: k.text.optional(), placeholder: k.label.optional() }),
    }),
    control({
        type: "drop-zone",
        group: "forms",
        heroui: "@heroui-pro/react DropZone",
        summary: `drop-zone { name, label, description?, accept?: [".csv", "text/plain"], multiple?, maxFiles?: 1-${UI_LIMITS.dropFiles}, onChange? } — files the person drops or picks; they go to the declared action that reads \`{{attachments.0.text}}\` (text files) or \`{{attachments.0.base64}}\`, at most ${UI_LIMITS.dropFileBytes / 1024} kB each, and never to the web`,
        example: { type: "drop-zone", name: "upload", label: "Add a CSV", description: "Up to 512 kB", accept: [".csv"], maxFiles: 1 },
        field: "files",
        shape: (k) => ({
            ...base(k),
            accept: k.z.array(k.z.string().regex(/^(\.[A-Za-z0-9]{1,10}|[a-z]+\/[a-z0-9.+*-]{1,40})$/)).max(10).optional(),
            multiple: k.z.boolean().optional(),
            maxFiles: k.z.number().int().min(1).max(UI_LIMITS.dropFiles).optional(),
        }),
    }),
];
//# sourceMappingURL=forms.js.map